import { type CellContext, type ColumnDef, createColumnHelper } from '@tanstack/react-table'
import type { SimpleVehicleProps } from '../../../types/types'
import { ChevronDown } from '../../../icons'
import DotsVertical from '../../../icons/DotsVertical'
import { formatDate } from '../../../helpers/formatDate'
import formatNumber from '../../../helpers/formatNumber'
import { CarParkTableDeviation } from './CarParkTableDeviation'
import { formatLeaseTerm, formatStatus } from '../../../helpers/formatTableValues'

const CarParkTableColumns = () => {
  const columnHelper = createColumnHelper<SimpleVehicleProps>()

  const defineColumn = <K extends keyof SimpleVehicleProps>(
    key: K,
    label: string,
    cell?: (info: CellContext<SimpleVehicleProps, SimpleVehicleProps[K]>) => React.ReactNode,
    enableSorting: boolean = false,
  ): ColumnDef<SimpleVehicleProps, any> => {
    return columnHelper.accessor(key as any, {
      header: ({ table }) => {
        if (key === 'registrationNumber') {
          return (
            <div className="flex items-center gap-2">
              <span className="flex items-center cursor-pointer" onClick={table.getToggleAllRowsExpandedHandler()}>
                <span
                  className={`transition-transform duration-200 w-4 h-4 flex items-center justify-center ${
                    table.getIsAllRowsExpanded() ? 'rotate-180' : 'rotate-0'
                  }`}
                >
                  <ChevronDown />
                </span>
              </span>
              <span className="text-old-secondary font-semibold text-sm">{label}</span>
            </div>
          )
        }
        return <span className="text-old-secondary font-semibold text-sm">{label}</span>
      },
      cell: cell ?? ((info) => info.getValue() || '-'),
      enableSorting,
      sortingFn: enableSorting ? 'datetime' : undefined,
    })
  }

  const columns: ColumnDef<SimpleVehicleProps, any>[] = [
    defineColumn('registrationNumber', 'Reg/Budsjett nr.', (info) => {
      const row = info.row
      const isExpanded = row.getIsExpanded()
      const toggle = row.getToggleExpandedHandler()
      const value = info.getValue() || row.original.budgetNo || '-'

      const { navigateToCar } = (info.table.options.meta || {}) as {
        navigateToCar?: (car: SimpleVehicleProps) => void
      }

      return (
        <div className={`flex items-center w-full gap-2 cursor-pointer select-none ${isExpanded && 'underline'}`}>
          <span className="flex items-center cursor-pointer" onClick={toggle}>
            <span
              className={`transition-transform duration-200 w-4 h-4 flex items-center justify-center ${
                isExpanded ? 'rotate-180' : 'rotate-0'
              }`}
            >
              <ChevronDown />
            </span>
          </span>
          <span onClick={() => navigateToCar?.(row.original)}>{value}</span>
        </div>
      )
    }),

    defineColumn('make', 'Bil', (info) => {
      const value = info.getValue()
      const model = info.row.original.model
      if (!value) return '-'

      const fullText = model ? `${value} ${model}` : `${value}`

      return <span title={fullText}>{fullText}</span>
    }),
    defineColumn('year', 'Årsmodell'),
    defineColumn('driverName', 'Sjåfør'),
    defineColumn('status', 'Status', (info) => {
      const value = info.getValue()
      return formatStatus(value)
    }),
    defineColumn('actualDeliveryDate', 'Løpetid', (info) => {
      const row = info.row.original
      return formatLeaseTerm(row.actualDeliveryDate, row.plannedReturnDate)
    }),
    defineColumn(
      'plannedReturnDate',
      'Avtaleslutt',
      (info) => {
        const value = info.getValue()
        if (!value) return '-'

        const formattedDate = formatDate(value) || '-'
        return formattedDate
      },
      true,
    ),
    defineColumn('contractMileage', 'Maks km', (info) => {
      const value = info.getValue()
      const formattedValue = value ? `${formatNumber(value)} km` : '-'
      return <span className="text-nowrap">{formattedValue}</span>
    }),
    defineColumn(
      'nextInspectionDate',
      'EU-kontrollfrist',
      (info) => {
        const value = info.getValue()
        if (!value) return '-'

        const formattedDate = formatDate(value)
        const date = new Date(value)
        const now = new Date()
        const diffTime = date.getTime() - now.getTime()
        const diffDays = diffTime / (1000 * 60 * 60 * 24)

        return (
          <div className="flex items-center">
            <span>{formattedDate}</span>
            {diffDays < 0 && <div className="w-4 h-4 rounded-full bg-old-content-danger ml-1" />}
            {diffDays >= 0 && diffDays <= 60 && <div className="w-4 h-4 rounded-full bg-old-warning ml-1" />}
          </div>
        )
      },
      true,
    ),
    defineColumn('replacementCar', 'Kobling bestilling', (info) => {
      const value = info.getValue()
      if (!value) return '-'

      const displayValue = value.registrationNumber || value.accountNo || value.budgetNo || '-'

      return displayValue
    }),
    defineColumn('deviations', '', (info) => {
      const value = info.getValue()
      if (!value || value.length === 0) return null
      return <CarParkTableDeviation deviations={value} />
    }),
    defineColumn('warnings', '', (info) => {
      const value = info.getValue()
      if (!value || value.length === 0) return null
      return <CarParkTableDeviation deviations={value} type="warning" />
    }),
    // defineColumn('engine', 'Egen kommentar', (info) => {
    //   const value = info.getValue() //TODO: handle if value
    //   const tempValue = 'Forrige service ble utført 01.01.2023'

    //   return (
    //     <>
    //       {tempValue ? (
    //         <HoverBox
    //           content={<div className="max-w-96">{tempValue}</div>}
    //           trigger={
    //             <div className="relative flex max-w-24 min-w-0">
    //               <span className="block truncate">{tempValue}</span>
    //             </div>
    //           }
    //         />
    //       ) : (
    //         <TertiaryButton
    //           onClick={() => {
    //             setSelectedCar(info.row.original as SimpleVehicleProps)
    //             setShowCommentModal(true)
    //           }}
    //         >
    //           Legg til
    //         </TertiaryButton>
    //       )}
    //     </>
    //   )
    // }),
    columnHelper.display({
      id: 'EditButton',
      header: () => null,
      cell: ({ row, table }) => {
        const { openEdit } = (table.options.meta || {}) as {
          openEdit?: (car: SimpleVehicleProps) => void
        }
        return (
          <div className="flex justify-end hover:cursor-pointer" onClick={() => openEdit?.(row.original)}>
            <DotsVertical />
          </div>
        )
      },
    }),
  ]

  return columns
}

export default CarParkTableColumns
